import type { ReactNode } from 'react';
import { color, font } from '../styles/theme';

/**
 * The centred card every signed-out screen sits in — sign in, request a reset,
 * set a password. Shared so the three cannot drift apart.
 */
export function AuthShell({
  title,
  sub,
  onSubmit,
  children,
  footnote,
}: {
  title: string;
  sub: string;
  onSubmit?: (e: React.FormEvent) => void;
  children: ReactNode;
  footnote?: ReactNode;
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: color.bg,
        padding: 20,
      }}
    >
      <form
        onSubmit={onSubmit}
        className="fade-in"
        style={{
          width: 'min(400px, 100%)',
          background: color.white,
          border: `1px solid ${color.line}`,
          borderRadius: 24,
          padding: 34,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span
            style={{
              display: 'flex',
              width: 48,
              height: 48,
              borderRadius: 14,
              background: color.tint,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src="/assets/enact-logo.png"
              alt=""
              style={{ width: 38, mixBlendMode: 'multiply' }}
            />
          </span>
          <div>
            <h1
              style={{
                fontFamily: font.display,
                fontWeight: 600,
                fontSize: 21,
                color: color.navy,
                margin: 0,
              }}
            >
              {title}
            </h1>
            <p style={{ fontSize: 13, color: color.muted, margin: '3px 0 0' }}>{sub}</p>
          </div>
        </div>

        {children}

        {footnote && (
          <p style={{ fontSize: 11.5, color: color.muted, margin: 0, textAlign: 'center' }}>
            {footnote}
          </p>
        )}
      </form>
    </div>
  );
}

/** The rules the API enforces, shown live as someone types. */
export function PasswordChecklist({ problems, dirty }: { problems: string[]; dirty: boolean }) {
  const rules = [
    { label: 'At least 12 characters', key: 'Use at least 12 characters' },
    { label: 'A capital letter', key: 'Include a capital letter' },
    { label: 'A number', key: 'Include a number' },
    { label: 'A special character, like ! ? # or @', key: 'Include a special character, like ! ? # or @' },
    { label: 'No leading or trailing spaces', key: 'Remove leading or trailing spaces' },
    { label: 'More than one distinct character', key: 'Use more than one distinct character' },
  ];
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 4 }}>
      {rules.map((r) => {
        const failed = problems.includes(r.key);
        const met = dirty && !failed;
        return (
          <li
            key={r.key}
            style={{
              fontSize: 12,
              color: !dirty ? color.muted : met ? color.green : color.red,
              display: 'flex',
              gap: 7,
            }}
          >
            <span aria-hidden style={{ width: 12 }}>{met ? '✓' : '·'}</span>
            {r.label}
          </li>
        );
      })}
    </ul>
  );
}
